import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenTool, RefreshCw, Clock, AlertTriangle, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { firmasAPI } from '../api';
import CancelarFirmaDialog from './CancelarFirmaDialog';
import CompartirEnlaceFirma from './CompartirEnlaceFirma';
import { toast } from '../hooks/use-toast';

const FirmasPendientesPanel = () => {
  const navigate = useNavigate();
  const [datos, setDatos] = useState({ pendientes: [], caducadas: [], total: 0 });
  const [cargando, setCargando] = useState(true);
  const [renovando, setRenovando] = useState(null);
  const [gestionar, setGestionar] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const res = await firmasAPI.pendientes();
      setDatos(res.data || { pendientes: [], caducadas: [], total: 0 });
    } catch (e) {
      setDatos({ pendientes: [], caducadas: [], total: 0 });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const refrescar = () => cargar();
    window.addEventListener('lexduo:firmas', refrescar);
    return () => window.removeEventListener('lexduo:firmas', refrescar);
  }, [cargar]);

  const renovar = async (id) => {
    setRenovando(id);
    try {
      const res = await firmasAPI.renovar(id, { enviar_email: true });
      toast({
        title: res.data?.email_enviado ? 'Enlace reenviado al cliente' : 'Enlace nuevo creado',
        description: res.data?.email_enviado
          ? `Enviado a ${res.data?.email_enviado_a || 'el cliente'}`
          : 'No se pudo enviar el email: ábrelo desde el expediente para copiar el enlace',
        variant: res.data?.email_enviado === false ? 'destructive' : 'default',
      });
      cargar();
    } catch (e) {
      toast({ title: 'No se pudo renovar', description: e?.response?.data?.detail || 'Error inesperado',
              variant: 'destructive' });
    } finally {
      setRenovando(null);
    }
  };

  if (cargando || datos.total === 0) return null;

  const filas = [...(datos.caducadas || []), ...(datos.pendientes || [])];

  return (
    <Card className="border-amber-200" data-testid="panel-firmas-pendientes">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PenTool className="w-4 h-4 text-indigo-600" />
          Firmas pendientes
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{datos.total}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {filas.slice(0, 8).map((f) => (
          <div key={f.id} className="flex items-center gap-3 py-2 border-b last:border-0"
               data-testid={`panel-firma-${f.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">{f.nombre_documento}</div>
              <div className="text-xs text-gray-500 truncate">
                {f.firmante_nombre || 'cliente'}
                {f.expediente_numero ? ` · ${f.expediente_numero}` : ''}
                {f.email_enviado_a ? ` · ${f.email_enviado_a}` : ' · enlace manual'}
              </div>
            </div>
            {f.estado === 'caducada' ? (
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px] flex-shrink-0">
                <AlertTriangle className="w-3 h-3 mr-1" /> Caducado
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] flex-shrink-0">
                <Clock className="w-3 h-3 mr-1" />
                {f.dias_esperando === 0 ? 'hoy' : `${f.dias_esperando} d`}
              </Badge>
            )}
            <CompartirEnlaceFirma firma={f} compacto />
            <Button size="sm" variant="ghost" className="h-7 px-2 flex-shrink-0"
                    title="Generar y reenviar un enlace nuevo"
                    disabled={renovando === f.id} onClick={() => renovar(f.id)}
                    data-testid={`panel-firma-renovar-${f.id}`}>
              <RefreshCw className={`w-3.5 h-3.5 text-indigo-600 ${renovando === f.id ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 flex-shrink-0"
                    title="Cancelar o eliminar la petición"
                    onClick={() => setGestionar(f)}
                    data-testid={`panel-firma-cancelar-${f.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-600" />
            </Button>
            {f.expediente_id && (
              <Button size="sm" variant="ghost" className="h-7 px-2 flex-shrink-0"
                      title="Ir al expediente"
                      onClick={() => navigate(`/expedientes/${f.expediente_id}`)}
                      data-testid={`panel-firma-ir-${f.id}`}>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </Button>
            )}
          </div>
        ))}
        {filas.length > 8 && (
          <div className="text-xs text-gray-500 pt-1">y {filas.length - 8} más…</div>
        )}
        <CancelarFirmaDialog
          firma={gestionar}
          open={!!gestionar}
          onOpenChange={(v) => { if (!v) setGestionar(null); }}
          onDone={() => { setGestionar(null); cargar(); }}
        />
      </CardContent>
    </Card>
  );
};

export default FirmasPendientesPanel;
